import { auth, onAuthStateChanged } from "@appally/lib/data/firebase";
import { Action, combineReducers, configureStore } from "@reduxjs/toolkit";
import { PersistConfig, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { assessmentApi } from "./api/assessmentApi";
import { managerApi } from "./api/managerApi";
import { organisationApi } from "./api/organisationApi";
import { questionsApi } from "./api/questionsApi";
import { userApi } from "./api/userApi";
import assessmentReducer from "./features/assessmentSlice";
import recommendationsReducer from "./features/recommendationsSlice";
import userReducer from "./features/userSlice";
import temporaryAnswersSlice from "./slices/temporaryAnswersSlice";

const rootReducer = combineReducers({
  // Legacy
  user: userReducer,
  assessment: assessmentReducer,
  recommendations: recommendationsReducer,

  // API routes
  [assessmentApi.reducerPath]: assessmentApi.reducer,
  [questionsApi.reducerPath]: questionsApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [managerApi.reducerPath]: managerApi.reducer,
  [organisationApi.reducerPath]: organisationApi.reducer,

  // Slices
  temporaryAnswers: temporaryAnswersSlice,
});

export type RootReducer = ReturnType<typeof rootReducer>;

const persistConfig: PersistConfig<RootReducer> = {
  key: "storePersistor",
  storage,
  whitelist: ["temporaryAnswers"],
  debug: true,
};

const persistedReducer = persistReducer<RootReducer, Action>(
  persistConfig,
  rootReducer,
);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    })
      .concat(questionsApi.middleware)
      .concat(userApi.middleware)
      .concat(assessmentApi.middleware)
      .concat(managerApi.middleware)
      .concat(organisationApi.middleware);
  },
});

export const persistor = persistStore(store);

if (typeof window !== "undefined" && auth) {
  let currentUid: string | null | undefined;

  onAuthStateChanged(auth, (user) => {
    const nextUid = user?.uid ?? null;

    if (currentUid === undefined) {
      currentUid = nextUid;
      return;
    }

    if (currentUid === nextUid) return;
    currentUid = nextUid;

    store.dispatch(
      userApi.util.invalidateTags(["User", "SupportedUsers", "Helper"]),
    );
    store.dispatch(
      assessmentApi.util.invalidateTags([
        "Assessments",
        "AssessmentStats",
        "Solutions",
        "Products",
        "Reports",
        "SelfStrategies",
      ]),
    );
    store.dispatch(
      managerApi.util.invalidateTags(["Assessments", "AssessmentSettings"]),
    );
    store.dispatch(
      organisationApi.util.invalidateTags([
        "Organisation",
        "AssessmentSolution",
        "User",
        "ManagerAssessments",
      ]),
    );
  });
}

// Get the type of our store variable
export type AppStore = typeof store;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore["getState"]>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = AppStore["dispatch"];
